/*
 * A visit's card on the schedule: click a visit and it opens beside it, without leaving the schedule, like the old
 * Steward's (05-calendar.js vpopHTML, owner 2026-10-09 with Jobber's as the model). It shows the client and job, Completed,
 * the team (add and take people off this visit), where, when it starts and ends, the line items and total, with Edit
 * (the schedule sheet) and Details (the visit's card pulled out on the right, VisitDrawer; the full visit page on a phone). On a phone it is a sheet from the bottom. Escape, the X or a
 * click outside closes it. On a computer, drag it by its top (the Visit row or the title) to move it out of the way.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import type { VisitCard, VisitDetail } from "@steward/shared";
import { type PointerEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useCompleteVisit, usePeople, useReopenVisit, useVisit, useVisitCrew } from "../api/hooks";
import { useSession } from "../app/session";
import { dayDate, moneyExact, shortDate, timeRange } from "./format";
import { DirectionsButton } from "./Directions";
import { Icon } from "./Icon";
import { useToast } from "./overlays";
import { Button, IconButton, Skeleton } from "./parts";
import { errText } from "./work";
import { unzoom } from "../app/fit-wide";

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? "")
    .slice(0, 2)
    .join("")
    .toUpperCase();

export function VisitPeek({
  visit,
  at,
  today,
  onClose,
  onEdit,
  onDetails,
}: {
  visit: VisitCard;
  /** Where the visit sits on screen, to open beside it. */
  at: DOMRect;
  today: string;
  onClose: () => void;
  onEdit: (v: VisitCard) => void;
  /** Opens the pulled-out card. Without it, or on a phone, Details goes to the visit page. */
  onDetails?: (v: VisitCard) => void;
}) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [ref, setRef] = useState(visit.ref);
  const detail = useVisit(ref);
  const people = usePeople(company.id, can("visit.update"));
  const complete = useCompleteVisit();
  const reopen = useReopenVisit();
  const crew = useVisitCrew();
  const [adding, setAdding] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  /** Set once it is dragged, so it stays where it was put while the details load. */
  const moved = useRef(false);
  const phone = window.matchMedia("(max-width: 520px)").matches;

  useEffect(() => {
    setRef(visit.ref);
    moved.current = false;
  }, [visit.ref]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const click = (e: globalThis.MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener("keydown", key);
    // After this click is done, so the click that opened it does not close it.
    const t = setTimeout(() => window.addEventListener("mousedown", click));
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", key);
      window.removeEventListener("mousedown", click);
    };
  }, [onClose]);
  /** Beside the visit (to its right, or its left when there is no room), kept on screen. */
  useLayoutEffect(() => {
    if (phone || !box.current || moved.current) return;
    const w = box.current.offsetWidth || 360;
    const h = box.current.offsetHeight || 420;
    const W = window.innerWidth;
    const H = window.innerHeight;
    const left = at.right + 8 + w <= W - 8 ? at.right + 8 : Math.max(8, at.left - 8 - w);
    setPos({ left, top: Math.min(Math.max(8, at.top), Math.max(8, H - h - 8)) });
  }, [at, phone, detail.data]);

  /** Drag by the top. Buttons and links there still click as usual. Kept on screen. */
  const drag = (e: PointerEvent<HTMLElement>) => {
    if (phone || e.button !== 0 || !pos || !box.current || (e.target as HTMLElement).closest("button, a")) return;
    e.preventDefault();
    const el = box.current;
    const dx = e.clientX - pos.left;
    const dy = e.clientY - pos.top;
    const move = (m: globalThis.PointerEvent) => {
      moved.current = true;
      setPos({
        left: Math.max(8, Math.min(m.clientX - dx, window.innerWidth - el.offsetWidth - 8)),
        top: Math.max(8, Math.min(m.clientY - dy, window.innerHeight - el.offsetHeight - 8)),
      });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      el.classList.remove("dragging");
    };
    el.classList.add("dragging");
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const v: VisitCard | VisitDetail = detail.data ?? visit;
  const d = detail.data;
  const done = v.status === "done";
  const editable = can("visit.update");
  const fail = (e: unknown) => toast(errText(e), { kind: "error" });
  const follow = (x: VisitDetail) => setRef(x.ref);
  const setCrew = (ids: string[], words: string) => {
    const before = v.crew.map((c) => c.id);
    crew.mutate(
      { ref: v.ref, crew: ids, scope: "this" },
      {
        onSuccess: (x) => {
          follow(x);
          toast(words, { undo: () => crew.mutate({ ref: x.ref, crew: before, scope: "this" }, { onSuccess: follow }) });
        },
        onError: fail,
      },
    );
  };
  const others = (people.data ?? []).filter((p) => p.active && !v.crew.some((c) => c.id === p.id));
  const total = d ? d.lines.reduce((s, l) => s + l.amountCents, 0) : v.totalCents;

  return (
    <div
      ref={box}
      className={`vpeek${phone ? " sheet" : ""}`}
      role="dialog"
      aria-label="Visit"
      style={pos ? { left: unzoom(pos.left), top: unzoom(pos.top) } : phone ? undefined : { visibility: "hidden" }}
    >
      <div className="vpeek-head" onPointerDown={drag}>
        <span className="muted small">Visit</span>
        <IconButton icon="close" label="Close" onClick={onClose} />
      </div>
      <h3 className="vpeek-title" onPointerDown={drag}>
        {v.clientName} - {v.jobName}
      </h3>
      {done || v.date <= today ? (
        <button
          type="button"
          className="vpeek-check"
          aria-pressed={done}
          disabled={!editable || complete.isPending || reopen.isPending || v.status === "skipped" || v.status === "cancelled"}
          onClick={() =>
            done
              ? reopen.mutate(v.ref, {
                  onSuccess: (x) => {
                    follow(x);
                    toast("Visit reopened");
                  },
                  onError: fail,
                })
              : complete.mutate(
                  { ref: v.ref },
                  {
                    onSuccess: (x) => {
                      follow(x);
                      toast(`${v.clientName} marked done`, { undo: () => reopen.mutate(x.ref, { onSuccess: follow }) });
                    },
                    onError: fail,
                  },
                )
          }
        >
          <span className="box">{done ? <Icon name="check" size={14} /> : null}</span>
          Completed
        </button>
      ) : null}
      {v.moved ? <span className="chip">Moved here from {shortDate(v.originalDate)}</span> : null}
      {v.unconfirmed ? <span className="chip">Not confirmed. Waiting on the client to sign the link.</span> : null}
      <div className="vpeek-sec">
        <b>Details</b>
        <span>
          <Link to="/clients/$id" params={{ id: v.clientId }}>
            {v.clientName}
          </Link>{" "}
          -{" "}
          <Link to="/jobs/$id" params={{ id: v.jobId }}>
            {v.jobName}
          </Link>
        </span>
      </div>
      <div className="vpeek-sec">
        <b>Team</b>
        <div className="vpeek-team">
          {editable ? (
            <button
              type="button"
              className="vpeek-add"
              aria-expanded={adding}
              aria-label="Add someone to this visit"
              onClick={() => setAdding(!adding)}
            >
              <Icon name="plus" size={14} />
            </button>
          ) : null}
          {v.crew.length ? (
            v.crew.map((c) => (
              <span key={c.id} className="vpeek-person" style={c.color ? { ["--person" as string]: c.color } : undefined}>
                <i>{initials(c.name)}</i>
                {c.name}
                {editable ? (
                  <button
                    type="button"
                    aria-label={`Take ${c.name} off this visit`}
                    onClick={() =>
                      setCrew(
                        v.crew.filter((x) => x.id !== c.id).map((x) => x.id),
                        `${c.name} is off this visit`,
                      )
                    }
                  >
                    <Icon name="close" size={12} />
                  </button>
                ) : null}
              </span>
            ))
          ) : (
            <span className="muted small">Nobody set</span>
          )}
        </div>
        {adding ? (
          others.length ? (
            <select
              className="select"
              aria-label="Add someone to this visit"
              value=""
              onChange={(e) => {
                const p = others.find((x) => x.id === e.target.value);
                if (!p) return;
                setAdding(false);
                setCrew([...v.crew.map((c) => c.id), p.id], `${p.name} is on this visit`);
              }}
            >
              <option value="">Add someone</option>
              {others.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="muted small">Everyone is on it already.</span>
          )
        ) : null}
      </div>
      {v.address ? (
        <div className="vpeek-sec">
          <b>Location</b>
          <DirectionsButton address={v.address} className="link">
            {v.address}
          </DirectionsButton>
        </div>
      ) : null}
      <div className="vpeek-grid">
        <div className="vpeek-sec">
          <b>Start</b>
          <span>{dayDate(v.date)}</span>
          <span>{v.startTime ? timeRange(v.startTime, null) : <span className="muted">No set time</span>}</span>
        </div>
        {v.endTime ? (
          <div className="vpeek-sec">
            <b>End</b>
            <span>{dayDate(v.date)}</span>
            <span>{timeRange(v.endTime, null)}</span>
          </div>
        ) : null}
      </div>
      <div className="vpeek-sec">
        <b>Line items</b>
        {d ? (
          <div className="vpeek-lines">
            {d.lines.map((l) => (
              <div key={l.id} className="vpeek-line">
                <span>{l.description || "Service"}</span>
                <span className="num">{moneyExact(l.amountCents)}</span>
              </div>
            ))}
            <div className="vpeek-line total">
              <b>Total</b>
              <b className="num">{moneyExact(total)}</b>
            </div>
          </div>
        ) : (
          <Skeleton rows={2} />
        )}
      </div>
      <div className="vpeek-btns">
        {can("schedule.update") && !done ? (
          <Button type="secondary" onClick={() => onEdit(v)}>
            Edit
          </Button>
        ) : null}
        <Button
          onClick={() => (onDetails && !phone ? onDetails(v) : void navigate({ to: "/visits/$ref", params: { ref: v.ref } }))}
        >
          Details
        </Button>
      </div>
    </div>
  );
}
